import React from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Icon, OptionalSection, ToastContext, Table, Message, TooltipHelp } from '@weezevent/nacre';
import { fetcher } from '@weezevent/weezjs-core';
import { PollingImportRequest, tFallback } from './utils';
import { ImportProcess } from '../../../../nacre/importController';
import LoadingCard from '../../../../components/loadingCard';

import { useTranslation } from 'react-i18next';
import css from './imports-steps.module.css';
import useAppContext from '../../../../context/useAppContext';

export const AnalysisStep = ({
    organization,
    event,
    typeKeys = ['attendees'],
    triggerAnalysis = false,
    redirectUrl,
    form,
    setForm,
    setFormObject,
    onCardDone,
    onDone,
    openModal
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [importReady, setImportReady] = React.useState(true);
    const [displayErrorsReport, setDisplayErrorsReport] = React.useState(false);
    const [context, setContext] = useAppContext();
    const [loadingAnalysis, setLoadingAnalysis] = React.useState(false);

    const reportValues = React.useMemo(() => {
        const { created, invalid, error } = form.requestDatas._summary;
        const summary = { created, invalid, error, total: created + invalid + error };
        return { ...summary, updated: form.requestDatas._data.attendee_update?.count || 0 };
    }, [form]);

    const _onDone = React.useCallback(
        requestDatas => {
            onDone(requestDatas, onCardDone);
        },
        [onCardDone, onDone]
    );

    const loadImportAnalysis = React.useCallback(() => {
        setLoadingAnalysis(true);
        const onSuccess = resp => {
            setLoadingAnalysis(false);
            setFormObject({ ...form, importRequest: resp, requestDatas: new ImportProcess(resp) });
        };
        const onError = resp => {
            setLoadingAnalysis(false);
        };
        PollingImportRequest(fetcher, form, onSuccess, onError, false);
    }, [form, setFormObject]);

    const gotReport = React.useMemo(() => form.requestDatas._summary.created || form.requestDatas._summary.invalid || form.requestDatas._summary.error, [form]);

    React.useEffect(() => {
        if (triggerAnalysis) {
            loadImportAnalysis();
        }
    }, [triggerAnalysis]);

    const handlePatchImportRequest = React.useCallback(() => {
        fetcher
            .patch(form.importRequest.url, { data: { step_name: 'import', status: 'ready' } })
            .then(() => {
                const contextSyncApi = { ...context, apiSync: true };
                toast.success(t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.analysis.import_in_progress`)));
                const onSuccessFileAnalysis = () => {
                    const requestDatas = { ...form.requestDatas };
                    _onDone(requestDatas);
                };
                const onSuccess = resp => {
                    const { summary } = resp;
                    const { created, error, invalid } = summary;
                    onSuccessFileAnalysis();
                    setImportReady(true);
                    setContext(contextSyncApi);
                    setForm('requestDatas', new ImportProcess(resp));
                    toast.success(t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.analysis.import_success`)), {
                        text: (
                            <ul>
                                <li>
                                    {t(
                                        tFallback(typeKeys, type => `ticket.${type}.imports.steps.analysis.import_success_created`),
                                        { count_created: created }
                                    )}
                                </li>
                                <li>
                                    {t(
                                        tFallback(typeKeys, type => `ticket.${type}.imports.steps.analysis.import_success_error`),
                                        { count_error: error }
                                    )}
                                </li>
                                <li>
                                    {t(
                                        tFallback(typeKeys, type => `ticket.${type}.imports.steps.analysis.import_success_invalid`),
                                        { count_invalid: invalid }
                                    )}
                                </li>
                            </ul>
                        )
                    });
                };
                const onError = () => {
                    setContext(contextSyncApi);
                    toast.error(t('ticket.common.toast_error'));
                };
                setImportReady(false);
                PollingImportRequest(fetcher, form, onSuccess, onError, true);
                history.push(redirectUrl);
            })
            .catch(e => {
                toast.error(t('ticket.common.toast_error'));
            });
    }, [organization, event, form, onCardDone, PollingImportRequest]);
    return (
        <div className={css['step-body']}>
            <LoadingCard loading={!importReady || loadingAnalysis || (triggerAnalysis && !gotReport)}>
                <ImportAnalysis typeKeys={typeKeys} datas={reportValues} />
                {reportValues.invalid >= 1 && (
                    <>
                        <Message title={t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.analysis.message.title`))} type="default">
                            {t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.analysis.message.description`))}
                        </Message>
                        <OptionalSection
                            className={css['analysis_report_section']}
                            label={t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.analysis.report.display`))}
                            labelOpen={t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.analysis.report.hide`))}
                            onToggle={() => setDisplayErrorsReport(!displayErrorsReport)}
                            open={displayErrorsReport}
                        >
                            <ErrorReportDetails typeKeys={typeKeys} errors={form.requestDatas._logs} />
                        </OptionalSection>
                    </>
                )}
                <div className={css['step-footer']}>
                    <Button
                        className={css['step-cta']}
                        primary
                        label={t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.analysis.cta.import_${reportValues.error > 0 ? 'errors' : 'valid'}`))}
                        onClick={handlePatchImportRequest}
                    />
                    <Button label={t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.data.cta`))} onClick={openModal} />
                </div>
            </LoadingCard>
        </div>
    );
};

const ImportAnalysis = ({ typeKeys, datas }) => {
    const { t } = useTranslation();

    const report = React.useMemo(() => {
        const checks = [
            { icon: 'chart', name: 'total', color: 'info', value: datas.total || 0 },
            { icon: 'check-circle', name: 'valid', color: 'valid', value: datas.created || 0 },
            { icon: 'info-circle', name: 'error', color: 'error', value: datas.invalid || 0 }
        ];
        const updated = { icon: 'refresh', name: 'updated', color: 'updated', value: datas.updated || 0 };

        if (datas.updated > 0) {
            checks.splice(2, 0, updated);
        }
        return checks;
    }, [datas]);

    return (
        <div className={css['analysis_infos']}>
            {report.map((ln, i) => (
                <p key={i}>
                    <Icon className={css[`analysis_icon_${ln.color}`]} name={ln.icon} />
                    <span className={css['analysis_message']}>
                        {t(
                            tFallback(typeKeys, type => `ticket.${type}.imports.steps.analysis.report.${ln.name}`),
                            { count: ln.value }
                        )}
                    </span>
                    {ln.name === 'updated' && <TooltipHelp title={t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.analysis.report.tooltip`))} />}
                </p>
            ))}
        </div>
    );
};

const ErrorReportDetails = ({ typeKeys, errors }) => {
    const { t } = useTranslation();

    const filteredErrors = React.useMemo(() => {
        const report = errors.filter(it => it.type === 'invalid');

        return report.reduce((acc, item) => {
            item.error.forEach(error => {
                const field = error.field;
                const message = error.message.split(' ').join('_');
                const existingGroup = acc.find(group => group.value === message);
                if (existingGroup) {
                    existingGroup.lines.push(item.line);
                } else {
                    acc.push({
                        value: message + field,
                        error: t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.errors.keys.${field.toLowerCase()}`)),
                        description: t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.errors.keys.${message.toLowerCase()}_desc`)),
                        lines: [item.line]
                    });
                }
            });
            return acc;
        }, []);
    }, [errors, typeKeys]);

    return (
        <Table useNew>
            <Table.Header>
                <Table.Tr>
                    <th className={`${css['analysis_error_table_small']}`}>{t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.errors.headers.lines`))}</th>
                    <th className={`${css['analysis_error_table_medium']}`}>{t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.errors.headers.error`))}</th>
                    <th className={`${css['analysis_error_table_medium']}`}>{t(tFallback(typeKeys, type => `ticket.${type}.imports.steps.errors.headers.description`))}</th>
                </Table.Tr>
            </Table.Header>
            <Table.TBody>
                {filteredErrors.map(err => (
                    <Table.Tr key={err.id} className={css['analysis_error_report']}>
                        <Table.Td>{err.lines.join(', ')}</Table.Td>
                        <Table.Td>{err.error}</Table.Td>
                        <Table.Td>{err.description}</Table.Td>
                    </Table.Tr>
                ))}
            </Table.TBody>
        </Table>
    );
};
